import { useEffect, useRef, useState } from 'react';
import AppModal from '../ui/AppModal';
import BtnTaratic from '../ui/BtnTaratic';
import { GhostSelect } from '../ui/GhostSelect';
import chatService from '../../services/chatService';
import { safeMediaUrl } from './MessageMedia';

const MAX_BYTES = 24 * 1024 * 1024;
export default function MediaComposer({ open, onClose, threadId, blocked }: {
  open: boolean; onClose: () => void; threadId: string; blocked: boolean;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState('image');
  const [recording, setRecording] = useState(false);
  const [requestingMic, setRequestingMic] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const alive = useRef(true);
  const busy = useRef(false);
  const generation = useRef(0);
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current++;
      if (recorder.current?.state === 'recording') recorder.current.stop();
      stream.current?.getTracks().forEach(track => track.stop());
    };
  }, []);
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const value = URL.createObjectURL(file);
    setPreview(value);
    return () => URL.revokeObjectURL(value);
  }, [file]);
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setSeconds(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);
  useEffect(() => {
    if ((seconds >= 300 || blocked) && recorder.current?.state === 'recording') recorder.current.stop();
  }, [seconds, blocked]);

  const choose = (value: File | null) => {
    setError('');
    if (!value) return;
    const type = value.type.split('/')[0];
    if (!['image', 'audio', 'video'].includes(type) || !value.size || value.size > MAX_BYTES || (type === 'image' && value.size > 8 * 1024 * 1024)) {
      setFile(null); setError('Selecciona una imagen de hasta 8 MB o audio/vídeo de hasta 24 MB.'); return;
    }
    setFile(value); setKind(type); setUrl('');
  };
  const record = async () => {
    if (requestingMic || recording || sending || blocked) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Este navegador no permite grabar audio. Abre la app con HTTPS o adjunta un archivo.'); return;
    }
    setRequestingMic(true); setError('');
    const current = ++generation.current;
    try {
      const input = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!alive.current || current !== generation.current || blockedRef.current) { input.getTracks().forEach(track => track.stop()); return; }
      stream.current = input;
      const mime = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'].find(type => MediaRecorder.isTypeSupported(type));
      const instance = new MediaRecorder(input, mime ? { mimeType: mime } : undefined);
      recorder.current = instance;
      const chunks: Blob[] = [];
      let size = 0;
      instance.ondataavailable = event => {
        if (event.data.size) { chunks.push(event.data); size += event.data.size; }
        if (size >= MAX_BYTES && instance.state === 'recording') instance.stop();
      };
      instance.onstop = () => {
        input.getTracks().forEach(track => track.stop());
        if (!alive.current || current !== generation.current) return;
        setRecording(false);
        choose(new File(chunks, 'nota-de-voz', { type: instance.mimeType || mime || 'audio/webm' }));
      };
      instance.onerror = () => {
        generation.current++;
        input.getTracks().forEach(track => track.stop());
        if (alive.current) { setRecording(false); setError('No se pudo grabar el audio. Inténtalo de nuevo.'); }
      };
      setSeconds(0); setFile(null); setUrl(''); setRecording(true); instance.start(1000);
    } catch {
      stream.current?.getTracks().forEach(track => track.stop());
      if (alive.current) { setRecording(false); setError('No se pudo acceder al micrófono. Revisa los permisos del navegador.'); }
    } finally { if (alive.current) setRequestingMic(false); }
  };
  const send = async () => {
    if (busy.current || blocked || recording || requestingMic) return;
    if (!file && !safeMediaUrl(url)) { setError('Selecciona un archivo o introduce una URL válida.'); return; }
    busy.current = true; setSending(true); setError('');
    try {
      await chatService.sendMessage(threadId, file ? { file } : { mediaUrl: url, mediaType: kind });
      if (alive.current) onClose();
    } catch (failure: any) {
      const detail = failure?.response?.data?.detail;
      if (alive.current) setError(typeof detail === 'string' ? detail : 'No se pudo confirmar el envío. Comprueba el chat antes de volver a intentarlo.');
    } finally { busy.current = false; if (alive.current) setSending(false); }
  };
  return <AppModal open={open} onClose={() => { if (!sending) onClose(); }} title="Enviar archivo o nota de voz"
    footer={<BtnTaratic loading={sending} disabled={blocked || recording || requestingMic || (!file && !url)} onClick={send}>Enviar</BtnTaratic>}>
    <div className="space-y-4">
      <label className="block">Archivo del dispositivo
        <input aria-label="Archivo del dispositivo" type="file" accept="image/jpeg,image/png,image/gif,image/webp,audio/*,video/mp4,video/quicktime,video/webm" disabled={sending || recording || requestingMic || blocked} className="mt-2 block w-full" onChange={event => { choose(event.target.files?.[0] || null); event.target.value = ''; }} />
      </label>
      <p className="text-xs">Imágenes: 8 MB. Audio y vídeo: 24 MB. Grabaciones: hasta 5 minutos.</p>
      <BtnTaratic disabled={sending || requestingMic || blocked} onClick={() => recording ? recorder.current?.stop() : void record()}>
        {recording ? `Detener grabación (${seconds}s)` : requestingMic ? 'Esperando micrófono…' : 'Grabar nota de voz'}
      </BtnTaratic>
      {file && <div className="space-y-2">
        <p>{file.name}</p>
        {kind === 'audio' ? <audio aria-label="Vista previa del audio" controls src={preview} className="max-w-full" /> : kind === 'video' ? <video controls src={preview} className="max-h-64 w-full" /> : <img src={preview} alt="Vista previa" className="max-h-64 object-contain" />}
        <BtnTaratic disabled={sending} onClick={() => setFile(null)}>Descartar archivo</BtnTaratic>
      </div>}
      {!file && !recording && <details><summary>Enviar desde una URL</summary><div className="mt-3 space-y-3">
        <GhostSelect disabled={sending} value={kind} onChange={setKind} options={[{value:'image',label:'Imagen'},{value:'audio',label:'Audio'},{value:'video',label:'Vídeo'}]} />
        <input aria-label="URL pública del archivo" type="url" disabled={sending} value={url} onChange={event => setUrl(event.target.value)} className="block w-full rounded-lg border p-3" placeholder="https://…" />
      </div></details>}
      {blocked && <p role="status">La ventana de respuesta está cerrada. El cliente debe enviar un nuevo mensaje.</p>}
      {error && <p role="alert">{error}</p>}
    </div>
  </AppModal>;
}
